import { Avatar as AvatarPrimitive } from 'radix-ui'
import * as React from 'react'

import { cn } from '@/lib/utils'

// The one place an avatar size is defined. Everything that takes a size and
// passes it to an Avatar should use this rather than restate the union, so a new
// size only has to be added here.
export type AvatarSize = 'default' | 'xs' | 'sm' | 'lg'

function Avatar({
  className,
  size = 'default',
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Root> & {
  size?: AvatarSize
}) {
  return (
    <AvatarPrimitive.Root
      data-slot="avatar"
      data-size={size}
      className={cn(
        `group/avatar relative flex size-8 shrink-0 overflow-hidden rounded-full
        select-none data-[size=lg]:size-10 data-[size=sm]:size-6
        data-[size=xs]:size-4 shadow`,
        className,
      )}
      {...props}
    />
  )
}

function AvatarImage({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Image>) {
  return (
    <AvatarPrimitive.Image
      data-slot="avatar-image"
      className={cn('aspect-square size-full', className)}
      {...props}
    />
  )
}

function AvatarFallback({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Fallback>) {
  return (
    <AvatarPrimitive.Fallback
      data-slot="avatar-fallback"
      className={cn(
        `flex size-full items-center justify-center rounded-full bg-muted
        text-sm text-muted-foreground group-data-[size=sm]/avatar:text-xs
        group-data-[size=xs]/avatar:text-[0.5rem]`,
        className,
      )}
      {...props}
    />
  )
}

function AvatarBadge({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="avatar-badge"
      className={cn(
        `absolute right-0 bottom-0 z-10 inline-flex items-center justify-center
        rounded-full bg-primary text-primary-foreground ring-2 ring-background
        select-none`,
        `group-data-[size=xs]/avatar:size-1.5
        group-data-[size=xs]/avatar:[&>svg]:hidden`,
        `group-data-[size=sm]/avatar:size-2
        group-data-[size=sm]/avatar:[&>svg]:hidden`,
        `group-data-[size=default]/avatar:size-2.5
        group-data-[size=default]/avatar:[&>svg]:size-2`,
        `group-data-[size=lg]/avatar:size-3
        group-data-[size=lg]/avatar:[&>svg]:size-2`,
        className,
      )}
      {...props}
    />
  )
}

function AvatarGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="avatar-group"
      className={cn(
        `group/avatar-group flex -space-x-2 *:data-[slot=avatar]:ring-2
        *:data-[slot=avatar]:ring-background`,
        className,
      )}
      {...props}
    />
  )
}

// Takes its size from the avatars it sits among, since it's their sibling rather
// than their child — but only inside an AvatarGroup, and only when they all agree.
const inheritedCountSize = `size-8 [&>svg]:size-4
  group-has-data-[size=lg]/avatar-group:size-10
  group-has-data-[size=sm]/avatar-group:size-6
  group-has-data-[size=xs]/avatar-group:size-4
  group-has-data-[size=lg]/avatar-group:[&>svg]:size-5
  group-has-data-[size=sm]/avatar-group:[&>svg]:size-3
  group-has-data-[size=xs]/avatar-group:[&>svg]:size-2`

// Picked in JS rather than layered on top of the inherited classes above: a
// `group-has-*` variant compiles to a more specific selector than a plain
// `data-*` one, so it would win the cascade whatever the order.
const countSizes: Record<AvatarSize, string> = {
  xs: 'size-4 [&>svg]:size-2',
  sm: 'size-6 [&>svg]:size-3',
  default: 'size-8 [&>svg]:size-4',
  lg: 'size-10 [&>svg]:size-5',
}

function AvatarGroupCount({
  className,
  size,
  ...props
}: React.ComponentProps<'div'> & {
  // Same sizes as Avatar. Left unset it follows its siblings, which is what a
  // group of uniformly sized avatars wants; set it when there's no group to read
  // from, or to say it outright.
  size?: AvatarSize
}) {
  return (
    <div
      data-slot="avatar-group-count"
      data-size={size}
      className={cn(
        `relative flex shrink-0 items-center justify-center rounded-full
        bg-gray-200 text-sm text-muted-foreground ring-2 ring-background`,
        size ? countSizes[size] : inheritedCountSize,
        className,
      )}
      {...props}
    />
  )
}

export {
  Avatar,
  AvatarBadge,
  AvatarFallback,
  AvatarGroup,
  AvatarGroupCount,
  AvatarImage,
}
